import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import type { Period, PeriodKey } from '../utils/resolution'
import { PERIODS, periodKeyOf } from '../utils/resolution'
import { CHART_COLORS } from '../utils/chartColors'
import { IndicatorPicker } from './IndicatorPicker'
import { FadingScrollRow } from './FadingScrollRow'
import { styleGroupOf } from './chartSettings'
import { CHART_TYPES, chartTypeIcon, type ChartTypeEntry } from './chartTypes'
import { BetaTag } from './BetaTag'
import { Popover, PopoverItem } from './toolbarPopover'
import { DrawingToolsMenu, type DrawingToolsMenuProps } from './DrawingToolsMenu'
import { PencilIcon } from './PencilIcon'
import { chipTextColor } from './toolbarChipColors'

/** Canonical period order, used to keep the inline favorites row stable
 *  regardless of the order keys were favorited in. */
const PERIOD_ORDER = Object.keys(PERIODS) as PeriodKey[]

/** Timeframe label metrics. The 18px line box matches the 18px icons in the
 *  chart-type / indicators buttons so every toolbar button is the same height
 *  (the font's default line-height otherwise renders the text box taller). */
const TIMEFRAME_LABEL_STYLE = { fontSize: 14, lineHeight: '18px' } as const

/* ------------------------------------------------------------------ */

/**
 * SURFACE CONVENTION (charts-core)
 *
 *   `bg-popover`                    — MENUS and PANELS: dropdowns, context menus,
 *                                   settings drawers, modals. The app's popover
 *                                   surface, so a dropdown looks identical
 *                                   whether the chart or the app shell owns it.
 *   `bg-field`                      — INPUTS AND SELECTS sitting on one of those
 *                                   panels. Slightly LIGHTER than the panel: a
 *                                   field is raised, not a hole. Do not use
 *                                   `bg-background` here — that is the app
 *                                   canvas, ~9 lightness points below a panel,
 *                                   and it reads as a void punched through it.
 *   `bg-[hsl(var(--chart-canvas))]` — CHART CHROME that sits ON the canvas: the
 *                                   drawing-bar column, the toolbar row, on-chart
 *                                   chips, and crosshair-tracking tooltips. These
 *                                   belong to the chart, not the menu system.
 *
 * A menu opening from a chart control used to render on a different grey from
 * the same menu opening from the panel header — the only difference being which
 * component owned it.
 *
 * These panels used to be `bg-input`, which was simultaneously the menu surface,
 * the field border, and (in name only) the field fill. Splitting `--field` out
 * is what let panels and the inputs on them stop fighting each other.
 */
const C = {
  bg: CHART_COLORS.canvas,
  surface: CHART_COLORS.surface,
  text: CHART_COLORS.text,
  textSecond: CHART_COLORS.textMuted,
  border: CHART_COLORS.border,
  primary: CHART_COLORS.primary,
  hover: CHART_COLORS.primaryFade,
}

/** Floating toolbar cluster: a bare flex row — each button carries its own
 *  glass chip styling, with nothing drawn behind them. */
export const GLASS_CLUSTER = 'pointer-events-auto flex items-center gap-1'

/* ------------------------------------------------------------------ */
/*  SVG Icons                                                          */
/* ------------------------------------------------------------------ */

const Icon = ({
  d,
  size = 18,
  strokeWidth = 1.8,
}: {
  d: string
  size?: number
  strokeWidth?: number
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={d} />
  </svg>
)

/** Italic ƒx glyph for the Indicators button ("functions"). */
const FxIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <text
      x="3"
      y="17"
      fontSize="16"
      fontStyle="italic"
      fontWeight="500"
      fontFamily="Georgia, 'Times New Roman', serif"
    >
      f
    </text>
    <text
      x="12.5"
      y="19.5"
      fontSize="10.5"
      fontStyle="italic"
      fontWeight="500"
      fontFamily="Georgia, 'Times New Roman', serif"
    >
      x
    </text>
  </svg>
)

/** Star — filled when the timeframe is a favorite, outline otherwise. */
const StarIcon = ({ filled, size = 13 }: { filled: boolean; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
)

// lucide "corner-left-down" — the chart's drill-down glyph. Exported so the
// body right-click "Drill into this day" row draws the EXACT same icon as the
// on-chart drill-down button (one source → the two can never diverge).
export const DRILL_DOWN_ICON_PATH = 'M14 15l-5 5-5-5 M20 4h-7a4 4 0 00-4 4v12'

const Icons = {
  indicators: 'M3 3v18h18M7 16l4-4 4 4 5-6',
  // Chart-type glyphs live in chartTypes.ts (CHART_TYPES / chartTypeIcon) —
  // they are shared with the body context menu and the app's panel-header pill.
  check: 'M20 6L9 17l-5-5',
  chevron: 'M6 9l6 6 6-6',
  screenshot:
    'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 17a4 4 0 100-8 4 4 0 000 8z',
  // lucide `settings-2` — two rails with knobs. NB: the SETTINGS-GEAR standard
  // elsewhere is now the lucide `settings` cog (EdgeChart's bottom-right gear +
  // the app's CellSettingsButton, 15px/1.8 — legible in practice); this sliders
  // glyph survives only for hosts that still pass `onSettings` to this Toolbar.
  settings: 'M20 7h-9 M14 17H5 M10 7a3 3 0 11-6 0 3 3 0 016 0z M20 17a3 3 0 11-6 0 3 3 0 016 0z',
  // lucide `maximize-2`, the same glyph as the workspace panel header's
  // Fullscreen action, so the chart bar and the panel chrome agree.
  fullscreen: 'M15 3h6v6 M21 3l-7 7 M3 21l7-7 M9 21H3v-6',
  bookmark: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z',
  trash: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  // lucide `save` (floppy): re-save a template with the chart's current studies.
  save: 'M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8',
  // lucide `layout-dashboard`, the same glyph as the workspace panel header's
  // Layout action (Chris, 2026-09-05: one layout icon everywhere).
  layout:
    'M4 3h5a1 1 0 011 1v7a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z M15 3h5a1 1 0 011 1v3a1 1 0 01-1 1h-5a1 1 0 01-1-1V4a1 1 0 011-1z M15 12h5a1 1 0 011 1v7a1 1 0 01-1 1h-5a1 1 0 01-1-1v-7a1 1 0 011-1z M4 16h5a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3a1 1 0 011-1z',
  cornerLeftDown: DRILL_DOWN_ICON_PATH,
  // lucide "corner-right-up" — the drill-up (close) affordance, inverse of drill-down.
  cornerRightUp: 'M10 9l5-5 5 5M4 20h7a4 4 0 004-4V4',
}

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Element | null
      // Popover menus portal to <body> (so the toolbar's horizontal-scroll
      // container can't clip them), so they're outside `ref`. Treat a click
      // inside any toolbar popover as inside — otherwise selecting a menu row
      // would dismiss the menu before the row's click lands.
      if (target?.closest?.('[data-toolbar-popover]')) return
      if (ref.current && !ref.current.contains(target as Node)) onClose()
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [ref, onClose])
}

/* ------------------------------------------------------------------ */
/*  Shared sub-components                                              */
/* ------------------------------------------------------------------ */

function ToolbarButton({
  children,
  title,
  dataAction,
  active,
  disabled,
  chip,
  chipText,
  pill,
  tone,
  onClick,
  onMouseDown,
  style: extraStyle,
}: {
  children: React.ReactNode
  title?: string
  /** Stable identifier for the button — used by popovers to recognise their own trigger. */
  dataAction?: string
  active?: boolean
  /** When true, the button renders dimmed and ignores onClick. Native hover
   *  styles also drop. Pair with a descriptive `title` to explain why. */
  disabled?: boolean
  /** Floating-toolbar look: each button is its own dark rounded chip with a
   *  bright icon (reference-app style), instead of a flat inline button. */
  chip?: boolean
  /** Flat button inside a chip group (the favourite timeframe segments): takes
   *  the chip text colours, so it reads on a light chart as well as a dark one. */
  chipText?: boolean
  /** Panel-header-pill look: a 24px segment, muted at rest, primary-tinted on
   *  hover, the metrics of the app's chart header controls. */
  pill?: boolean
  /** Accent tone. `'primary'` renders a solid purple fill (brighter on hover)
   *  with a white icon — a deliberately distinct affordance (e.g. Drill up). */
  tone?: 'primary'
  onClick?: (e: React.MouseEvent) => void
  onMouseDown?: (e: React.MouseEvent) => void
  style?: React.CSSProperties
}) {
  const [hover, setHover] = useState(false)
  const primaryTone = tone === 'primary'
  // Each chip is its own small glass tile floating directly over the chart —
  // a translucent light-on-dark fill (visibly distinct from the canvas) with
  // its own blur, no shared container behind the row.
  const chipStyle: React.CSSProperties = {
    minWidth: 22,
    height: 22,
    padding: '2px 5px',
    borderRadius: 5,
    background: primaryTone
      ? hover && !disabled
        ? 'hsl(var(--primary))'
        : 'hsl(var(--primary) / 0.85)'
      : !disabled && active
        ? C.hover
        : !disabled && hover
          ? 'hsl(var(--chart-text) / 0.18)'
          : 'hsl(var(--chart-text) / 0.1)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    // A notch brighter than the muted tone, but thin strokes — not bold.
    color: primaryTone
      ? 'hsl(var(--primary-foreground))'
      : disabled
        ? chipTextColor({ disabled })
        : active
          ? C.primary
          : chipTextColor({ hover }),
  }
  // Mirrors the app's `.panel-header-pill` rules: 24px tall, 4px radius,
  // muted glyph, primary tint (22%) on hover or while active.
  const pillStyle: React.CSSProperties = {
    height: 24,
    minWidth: 30,
    padding: '0 6px',
    borderRadius: 4,
    background: primaryTone
      ? hover && !disabled
        ? 'hsl(var(--primary))'
        : 'hsl(var(--primary) / 0.85)'
      : !disabled && (active || hover)
        ? 'hsl(var(--primary) / 0.22)'
        : 'transparent',
    color: primaryTone
      ? 'hsl(var(--primary-foreground))'
      : disabled
        ? C.textSecond
        : active || hover
          ? 'hsl(var(--foreground))'
          : 'hsl(var(--muted-foreground))',
  }
  const flatStyle: React.CSSProperties = {
    background: primaryTone
      ? hover && !disabled
        ? 'hsl(var(--primary))'
        : 'hsl(var(--primary) / 0.85)'
      : !disabled && (active || hover)
        ? C.hover
        : 'none',
    color: primaryTone
      ? 'hsl(var(--primary-foreground))'
      : disabled
        ? chipText
          ? chipTextColor({ disabled })
          : C.textSecond
        : active
          ? C.primary
          : chipText
            ? chipTextColor({ hover })
            : hover
              ? C.text
              : C.textSecond,
    borderRadius: 4,
    padding: '4px 6px',
  }
  return (
    <button
      title={title}
      data-action={dataAction}
      // Press feedback. `transition: all` below covers colour/background; the
      // scale-on-active lives in the shared `.chrome-press` rule so every chart
      // control responds to a click the same way.
      className="chrome-press"
      onClick={disabled ? undefined : onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition:
          'background-color var(--motion-fast) var(--ease-out), color var(--motion-fast) var(--ease-out), opacity var(--motion-fast) var(--ease-out)',
        ...(chip ? chipStyle : pill ? pillStyle : flatStyle),
        ...extraStyle,
      }}
    >
      {children}
    </button>
  )
}

/** A row in the timeframe dropdown: the period label (selects it) plus an
 *  optional favorite star. The star toggles favorite without selecting the
 *  period or closing the menu, so a user can star several in one pass. */
function PeriodMenuRow({
  period,
  active,
  favorite,
  showStar,
  onSelect,
  onToggleFavorite,
}: {
  period: Period
  active: boolean
  favorite: boolean
  showStar: boolean
  onSelect: () => void
  onToggleFavorite?: () => void
}) {
  return (
    <div
      data-period-row=""
      className="flex items-center rounded-sm pr-1 transition-colors hover:bg-accent"
    >
      <button
        onClick={onSelect}
        className="flex-1 rounded-sm px-2.5 py-1 text-left text-xs text-[hsl(var(--chart-text-muted))] transition-colors hover:text-[hsl(var(--chart-text))]"
      >
        <span className={active ? 'font-semibold text-primary' : ''}>{period.fullLabel}</span>
      </button>
      {showStar && onToggleFavorite && (
        <button
          title={favorite ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={favorite}
          onClick={(e) => {
            e.stopPropagation()
            onToggleFavorite()
          }}
          className={
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-sm transition-colors hover:text-[hsl(var(--chart-text))] ' +
            (favorite ? 'text-primary' : 'text-[hsl(var(--chart-text-muted))]')
          }
        >
          <StarIcon filled={favorite} />
        </button>
      )}
    </div>
  )
}

function PopoverGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--chart-text-muted)/0.7)]">
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Toolbar                                                            */
/* ------------------------------------------------------------------ */

export interface ToolbarTemplate {
  templateId: string
  name: string
}

export interface ToolbarProps {
  /**
   * Optional leading content rendered at the far left of the toolbar (before
   * the timeframe button), followed by a separator. Used to fold a chart's
   * title — e.g. `AAPL · 2024-01-05` — into the toolbar so it doubles as the
   * chart's single header instead of needing a separate bar above it.
   */
  title?: ReactNode
  period: Period
  chartType: string
  onPeriodChange: (p: Period) => void
  onChartTypeChange: (type: string) => void
  /** Chart types to list. Defaults to the full catalog; Beta entries carry a chip. */
  chartTypes?: readonly ChartTypeEntry[]
  onAddIndicator: (name: string, paneType: 'main' | 'sub', calcParams?: unknown[]) => void
  onScreenshot?: () => void
  onFullscreen?: () => void
  onSettings?: () => void
  settingsActive?: boolean
  /**
   * When set, renders a Layout button at the far right of the toolbar.
   * Used by multi-chart and (future) single charts that adopt a grid layout.
   */
  onLayout?: (e: React.MouseEvent) => void
  layoutActive?: boolean
  templates?: ToolbarTemplate[]
  onSaveTemplate?: (name: string) => void
  onLoadTemplate?: (templateId: string) => void
  onDeleteTemplate?: (templateId: string) => void
  /** Overwrite a saved template with the chart's current studies (save icon per row). */
  onUpdateTemplate?: (templateId: string) => void
  /** When true, the period (timeframe) selector is rendered disabled and
   *  cannot be opened. Use when the chart's timeframe is owned by an
   *  upstream binding (e.g. a Price Data node link) and changing it from
   *  the chart would be a no-op or misleading. */
  periodLocked?: boolean
  /** Tooltip shown on the disabled period button explaining why it's
   *  locked. Falls back to the default "Timeframe" title when not set. */
  periodLockedTooltip?: string
  /** Favorite-timeframe keys to surface inline in the toolbar (quick-access
   *  style). Rendered in canonical period order, not array order. When empty,
   *  the timeframe control stays the single-button dropdown. */
  favoriteTimeframes?: PeriodKey[]
  /** Toggle a period in/out of favorites — wires the star buttons in the
   *  dropdown and the inline row. When omitted, the favorites UI is disabled
   *  entirely and the control falls back to the plain single-button dropdown. */
  onToggleFavoriteTimeframe?: (key: PeriodKey) => void
  /** Opt-in: show an "Auto" entry atop the timeframe dropdown. The backtest
   *  grid uses it to hand a cell back to automatic timeframe assignment. */
  allowAuto?: boolean
  /** True when the active selection is "Auto" — renders the timeframe button
   *  label as "Auto" and marks the Auto row active. */
  isAuto?: boolean
  /** Called when the user picks the "Auto" row. */
  onSelectAuto?: () => void
  /**
   * Floating glass mode (EdgeChart): the toolbar renders as backdrop-blurred
   * pill clusters absolutely positioned over the chart canvas instead of an
   * in-flow row. Hosts that lay the toolbar above a cell grid (multi-chart,
   * history) keep the default in-flow layout.
   */
  floating?: boolean
  /**
   * In-flow shell style. `bar` is the original solid strip; `pill` renders
   * the controls as the workspace panel header's pill (24px segments, one
   * divider before the trailing slot, Layout then Fullscreen on the right) so
   * a chart bar that is not inside a panel still reads as the same header.
   */
  variant?: 'bar' | 'pill'
  /** When set, renders a drill-down button (corner-left-down) in the
   *  right-side tool cluster, after Layout. */
  onDrillDown?: () => void
  /** When set, renders a drill-up (close) button (corner-right-up, purple) in
   *  the right-side tool cluster, right after Layout — the inverse of the
   *  drill-down affordance. Used by drilled views to exit the drill.
   *
   *  NOTE: drill hosts now pin an always-visible `DrillUpChip` (below) at the
   *  active cell's top-right instead of passing `onDrillUp` here — the pinned
   *  chip is the drill-mode indicator and must not hide with the hover cluster.
   *  Those hosts pass `rightClusterOffset` to slide the hover cluster left of
   *  the pinned chip. `onDrillUp` remains supported for the in-cluster variant. */
  onDrillUp?: () => void
  /**
   * Floating mode: pixel offset applied to the RIGHT edge of the hover-revealed
   * tool cluster (Layout / screenshot / settings / …). Hosts that pin an
   * always-visible `DrillUpChip` at the cell's top-right pass ~28 so the
   * hover cluster lands immediately to the LEFT of the pinned chip, never
   * overlapping it. Defaults to 0 (cluster hugs the top-right corner).
   */
  rightClusterOffset?: number
  /**
   * Floating mode: pixel offset applied to the TOP of the hover-revealed tool
   * cluster, below the strip's first row. Hosts whose top-right corner is
   * taken by other chrome (a floating page header) drop the cluster into a
   * second row on the right. Defaults to 0 (cluster shares the first row).
   */
  rightClusterTop?: number
  /**
   * Floating mode: pixel width kept clear on the right of the first row, in
   * place of the default 64px (plus `rightClusterOffset`) corner reserve.
   * For hosts that float their own chrome over the first row's right end.
   */
  rightReserve?: number
  /**
   * Floating mode: top edge of the strip in px, in place of the default 6
   * (`top-1.5`). For hosts that drop the strip below chrome floating over the
   * chart's top edge.
   */
  rootTop?: number
  /**
   * Floating mode: extra content rendered as a wrapping flex item after the
   * left cluster (e.g. the OHLC header) — sits beside the chips when the row
   * has room, wraps underneath when it doesn't.
   */
  trailing?: ReactNode
  /** Floating mode: ref to the root strip, for hosts that measure its height
   *  (the overlay stack below shifts when `trailing` wraps to a second row). */
  rootRef?: React.Ref<HTMLDivElement>
  /**
   * Floating mode: hide the left control cluster (timeframe / type / ƒx /
   * templates) while keeping `trailing` (OHLC) and the right tool cluster.
   * Used when the workspace panel header hosts those controls instead.
   */
  controlsHidden?: boolean
  /**
   * Keep only the timeframe control (and the title) in the left cluster: no
   * chart type, indicators or templates. For a chart the user reads rather
   * than configures, like a University challenge.
   */
  timeframeOnly?: boolean
  /**
   * When provided, renders a pencil button at the END of the left cluster (so
   * it lands immediately before the `trailing` slot, which hosts the calendar)
   * opening a `DrawingToolsMenu` with the full drawing catalog. The props are
   * the same subset the floating `DrawingBar` takes, so a host wires one set of
   * handlers to both surfaces. Omit it and no button renders.
   */
  drawingTools?: Omit<DrawingToolsMenuProps, 'onClose' | 'inline'>
}

export function Toolbar(props: ToolbarProps) {
  const {
    title,
    period,
    chartType,
    chartTypes = CHART_TYPES,
    onPeriodChange,
    onChartTypeChange,
    onAddIndicator,
    onScreenshot,
    onFullscreen,
    onSettings,
    settingsActive,
    onLayout,
    layoutActive,
    templates,
    onSaveTemplate,
    onLoadTemplate,
    onDeleteTemplate,
    onUpdateTemplate,
    periodLocked,
    periodLockedTooltip,
    favoriteTimeframes,
    onToggleFavoriteTimeframe,
    allowAuto,
    isAuto,
    onSelectAuto,
    floating,
    variant = 'bar',
    onDrillDown,
    onDrillUp,
    rightClusterOffset,
    rightClusterTop,
    rightReserve,
    rootTop,
    trailing,
    rootRef,
    controlsHidden,
    timeframeOnly = false,
    drawingTools,
  } = props

  const [periodOpen, setPeriodOpen] = useState(false)
  const [chartTypeOpen, setChartTypeOpen] = useState(false)
  const [indicatorOpen, setIndicatorOpen] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const periodRef = useRef<HTMLDivElement>(null)
  const chartTypeRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const templateRef = useRef<HTMLDivElement>(null)
  const [drawingToolsOpen, setDrawingToolsOpen] = useState(false)
  const drawingToolsRef = useRef<HTMLDivElement>(null)

  useClickOutside(
    periodRef,
    useCallback(() => setPeriodOpen(false), []),
  )
  useClickOutside(
    chartTypeRef,
    useCallback(() => setChartTypeOpen(false), []),
  )
  useClickOutside(
    indicatorRef,
    useCallback(() => setIndicatorOpen(false), []),
  )
  useClickOutside(
    templateRef,
    useCallback(() => setTemplateOpen(false), []),
  )
  useClickOutside(
    drawingToolsRef,
    useCallback(() => setDrawingToolsOpen(false), []),
  )

  const periodEntries = Object.entries(PERIODS) as [PeriodKey, Period][]
  // Favorites are opt-in: only active when the host wires the toggle callback.
  const favoritesEnabled = onToggleFavoriteTimeframe != null
  const favoriteSet = new Set(favoriteTimeframes ?? [])
  // Inline favorites in canonical order (not the order they were starred in).
  const favoriteKeys = PERIOD_ORDER.filter((k) => favoriteSet.has(k))
  const currentKey = periodKeyOf(period)
  // Standard behavior: if the active timeframe isn't a favorite, show it as a
  // transient chip at the end of the row. It's derived from the current period,
  // so switching away (or favoriting it) makes it drop off on its own.
  const transientKey = currentKey != null && !favoriteSet.has(currentKey) ? currentKey : null
  const inlineKeys = transientKey != null ? [...favoriteKeys, transientKey] : favoriteKeys
  // Show the inline row only when there's something to show and the control
  // isn't locked (a locked timeframe stays the single disabled button).
  const showInlineFavorites = favoritesEnabled && favoriteKeys.length > 0 && !periodLocked

  // The dropdown body — shared by the favorites (chevron-anchored) and the
  // single-button layouts so the two stay identical.
  // Opt-in "Auto" row pinned to the top of the dropdown (hands the cell back
  // to automatic timeframe assignment). Rendered before the concrete periods.
  const autoRow =
    allowAuto && onSelectAuto ? (
      <button
        key="__auto__"
        type="button"
        data-testid="period-auto-row"
        data-period-row="auto"
        onClick={() => {
          onSelectAuto()
          setPeriodOpen(false)
        }}
        className="flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-accent"
      >
        <span className={isAuto ? 'font-semibold text-primary' : ''}>Auto</span>
      </button>
    ) : null

  const periodMenuRows = [
    autoRow,
    ...periodEntries.map(([key, p]) => (
      <PeriodMenuRow
        key={key}
        period={p}
        active={!isAuto && key === currentKey}
        favorite={favoriteSet.has(key)}
        showStar={favoritesEnabled}
        onSelect={() => {
          onPeriodChange(p)
          setPeriodOpen(false)
        }}
        onToggleFavorite={
          onToggleFavoriteTimeframe ? () => onToggleFavoriteTimeframe(key) : undefined
        }
      />
    )),
  ]

  // Floating mode renders every control as its own dark chip (reference-app
  // style): bigger buttons, brighter icons, bolder timeframe text, dropdown
  // chevrons, and no divider lines (the chip gaps do that job).
  const chip = !!floating
  const pill = !chip && variant === 'pill'
  const iconSize = chip || pill ? 15 : 18
  const iconStroke = chip || pill ? 1.5 : 1.8
  const tfLabelStyle = chip
    ? ({ fontSize: 12, fontWeight: 400, lineHeight: '16px' } as const)
    : TIMEFRAME_LABEL_STYLE
  // Inline favorite timeframes share ONE chip background (a segmented pill);
  // the individual period buttons render flat inside it.
  const favoritesGroupStyle: React.CSSProperties | undefined = chip
    ? {
        height: 22,
        borderRadius: 5,
        padding: '0 1px',
        background: 'hsl(var(--chart-text) / 0.1)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }
    : undefined
  const favoriteSegmentStyle: React.CSSProperties | undefined = chip
    ? { padding: '1px 5px', borderRadius: 4 }
    : undefined
  const separator = chip ? null : pill ? (
    <div className="w-px shrink-0 self-stretch bg-border" />
  ) : (
    <div className="mx-0.5 h-5 w-px shrink-0 bg-border" />
  )

  // Left cluster: timeframe / chart type / indicators / templates (+ title).
  // Right cluster: screenshot / settings / fullscreen / layout. Shared by both
  // layout shells so the floating and in-flow modes stay behaviorally identical.
  const leftControls = (
    <>
      {/* Optional leading title (e.g. "AAPL · 2024-01-05") — lets the toolbar
          serve as the chart's single header. */}
      {title != null && (
        <>
          <div
            data-testid="chart-toolbar-title"
            className={
              'flex items-center gap-1.5 text-[13px] font-semibold text-[hsl(var(--chart-text))] ' +
              // Chip mode: don't shrink — the ticker is a chip in the horizontal
              // scroll cluster, so it must keep its width and scroll WITH the
              // other chips rather than collapse under the timeframe. Docked mode
              // keeps the truncate-to-fit behaviour of the fixed-width header.
              (chip
                ? 'shrink-0'
                : pill
                  ? 'shrink-0 px-2 text-[12px] font-medium'
                  : 'min-w-0 overflow-hidden truncate pl-0.5 pr-1')
            }
          >
            {title}
          </div>
          {separator}
        </>
      )}
      {/* Timeframe control — favorites inline + "more" chevron, or a single
          button when there are no favorites (or the feature is off/locked). */}
      <div ref={periodRef} className="relative flex items-center gap-0.5">
        {showInlineFavorites ? (
          <div style={favoritesGroupStyle} className="flex items-center">
            {inlineKeys.map((key) => {
              const p = PERIODS[key]
              return (
                <ToolbarButton
                  key={key}
                  title={p.fullLabel}
                  active={key === currentKey}
                  style={favoriteSegmentStyle}
                  chipText={chip}
                  onClick={() => onPeriodChange(p)}
                >
                  <span style={tfLabelStyle}>{p.label}</span>
                </ToolbarButton>
              )
            })}
            {/* Chevron owns the Popover so the dropdown anchors under it. */}
            <div className="relative flex items-center">
              <ToolbarButton
                title="More timeframes"
                dataAction="period-more"
                active={periodOpen}
                style={favoriteSegmentStyle}
                chipText={chip}
                onClick={() => setPeriodOpen((v) => !v)}
              >
                <Icon d={Icons.chevron} size={chip ? 12 : 18} strokeWidth={iconStroke} />
              </ToolbarButton>
              {/* Anchor the dropdown's LEFT edge to the chevron so it opens
                  rightward into the toolbar's open space — anchoring the right
                  edge runs it off the left of the screen when few favorites
                  push the chevron near the left edge. */}
              {periodOpen && (
                <Popover anchor="bottom" align="left">
                  {periodMenuRows}
                </Popover>
              )}
            </div>
          </div>
        ) : (
          <ToolbarButton
            title={periodLocked ? (periodLockedTooltip ?? 'Timeframe') : 'Timeframe'}
            active={periodOpen}
            disabled={periodLocked}
            chip={chip}
            pill={pill}
            onClick={() => setPeriodOpen((v) => !v)}
          >
            <span style={tfLabelStyle}>{isAuto ? 'Auto' : period.label}</span>
          </ToolbarButton>
        )}
        {!showInlineFavorites && periodOpen && !periodLocked && (
          <Popover anchor="bottom">{periodMenuRows}</Popover>
        )}
      </div>

      {/* Chart type and indicators: left out when the host wants a chart
          you read, not configure (a University challenge). */}
      {!timeframeOnly && (
        <>
          {separator}

          {/* Chart type */}
          <div ref={chartTypeRef} className="relative">
            <ToolbarButton
              title="Chart type"
              active={chartTypeOpen}
              chip={chip}
              pill={pill}
              onClick={() => setChartTypeOpen((v) => !v)}
            >
              <Icon size={iconSize} d={chartTypeIcon(chartType)} />
            </ToolbarButton>
            {chartTypeOpen && (
              <Popover anchor="bottom">
                {chartTypes.map((ct, i) => {
                  // Family match, not string equality: with hollow/area now
                  // modifiers, a chart on `candle_stroke` must still mark Candles
                  // active and one on `area` must mark Line.
                  const active = styleGroupOf(chartType) === styleGroupOf(ct.name)
                  return (
                    <div
                      key={ct.name}
                      // Hairline rule between entries, not above the first.
                      className={
                        i > 0 ? 'border-t border-[hsl(var(--chart-text)/0.08)] pt-0.5 mt-0.5' : ''
                      }
                    >
                      <PopoverItem
                        onClick={() => {
                          // Re-picking the family a chart is already in would demote
                          // hollow candles to solid (or drop an area fill), so only
                          // switch when the family actually changes.
                          if (!active) onChartTypeChange(ct.name)
                          setChartTypeOpen(false)
                        }}
                      >
                        <span className="flex items-center gap-2">
                          <Icon size={15} d={chartTypeIcon(ct.name)} strokeWidth={1.6} />
                          <span className={active ? 'font-semibold text-primary' : ''}>
                            {ct.label}
                          </span>
                          {ct.beta && <BetaTag />}
                          {active && <Icon size={13} d={Icons.check} />}
                        </span>
                      </PopoverItem>
                    </div>
                  )
                })}
              </Popover>
            )}
          </div>

          {separator}

          {/* Indicators — ƒx "functions" button */}
          <div ref={indicatorRef} className="relative">
            <ToolbarButton
              title="Indicators"
              active={indicatorOpen}
              chip={chip}
              pill={pill}
              onClick={() => setIndicatorOpen((v) => !v)}
            >
              <FxIcon size={chip ? 16 : 18} />
            </ToolbarButton>
            {indicatorOpen && (
              // Portaled (like the other chip dropdowns) so the hide-panel toolbar's
              // overflow-y-hidden scroll container can't clip the picker. Uses the
              // picker's self-styled `inline` mode inside a chrome-only Popover.
              <Popover
                anchor="bottom"
                contentClassName="overflow-hidden rounded-md border border-border bg-popover shadow-md"
              >
                <IndicatorPicker
                  inline
                  onAdd={(name, paneType, calcParams) => onAddIndicator(name, paneType, calcParams)}
                  onClose={() => setIndicatorOpen(false)}
                />
              </Popover>
            )}
          </div>
        </>
      )}

      {/* Templates */}
      {onSaveTemplate && !timeframeOnly && (
        <>
          {separator}
          <div ref={templateRef} className="relative">
            <ToolbarButton
              title="Templates"
              active={templateOpen}
              chip={chip}
              pill={pill}
              onClick={() => setTemplateOpen((v) => !v)}
            >
              <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.bookmark} />
            </ToolbarButton>
            {templateOpen && (
              <Popover anchor="bottom">
                <div className="px-1.5 pb-1.5 pt-1">
                  <div className="flex gap-1">
                    <input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && templateName.trim()) {
                          onSaveTemplate(templateName.trim())
                          setTemplateName('')
                        }
                      }}
                      placeholder="Save as template..."
                      className="flex-1 rounded-sm border border-border bg-field px-2 py-1 text-[11px] text-[hsl(var(--chart-text))] outline-hidden placeholder:text-[hsl(var(--chart-text-muted))]"
                    />
                    <button
                      onClick={() => {
                        if (templateName.trim()) {
                          onSaveTemplate(templateName.trim())
                          setTemplateName('')
                        }
                      }}
                      className="rounded-sm bg-primary px-2 py-1 text-[11px] text-primary-foreground hover:bg-primary-hover"
                    >
                      Save
                    </button>
                  </div>
                </div>
                {templates && templates.length > 0 && (
                  <>
                    <div className="my-0.5 h-px bg-border" />
                    <PopoverGroupLabel>Saved Templates</PopoverGroupLabel>
                    {templates.map((t) => (
                      <div key={t.templateId} className="flex items-center">
                        <PopoverItem
                          onClick={() => {
                            onLoadTemplate?.(t.templateId)
                            setTemplateOpen(false)
                          }}
                        >
                          {t.name}
                        </PopoverItem>
                        {onUpdateTemplate && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              onUpdateTemplate(t.templateId)
                            }}
                            className="shrink-0 px-1 py-0.5 text-[hsl(var(--chart-text-muted)/0.6)] hover:text-[hsl(var(--chart-text))]"
                            title={`Save current chart to ${t.name}`}
                            aria-label={`Save current chart to ${t.name}`}
                          >
                            <Icon d={Icons.save} size={13} />
                          </button>
                        )}
                        {onDeleteTemplate && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteTemplate(t.templateId)
                            }}
                            className="shrink-0 px-1.5 py-0.5 text-[hsl(var(--chart-text-muted)/0.6)] hover:text-[hsl(var(--chart-text))]"
                            title="Delete template"
                          >
                            <Icon d={Icons.trash} size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </Popover>
            )}
          </div>
        </>
      )}

      {/* Drawing tools - the LAST left-cluster item, so it sits immediately
          before the `trailing` slot (the calendar) in every variant. Same
          catalog as the floating DrawingBar on the chart's left edge, reached
          from the top bar instead. */}
      {drawingTools && (
        <>
          {separator}
          <div ref={drawingToolsRef} className="relative">
            <ToolbarButton
              title="Drawing tools"
              dataAction="drawing-tools"
              active={drawingToolsOpen}
              chip={chip}
              pill={pill}
              onClick={() => setDrawingToolsOpen((v) => !v)}
            >
              <PencilIcon size={iconSize} />
            </ToolbarButton>
            {drawingToolsOpen && (
              <DrawingToolsMenu {...drawingTools} onClose={() => setDrawingToolsOpen(false)} />
            )}
          </div>
        </>
      )}
    </>
  )

  const hasRightTools = !!(
    onScreenshot ||
    onSettings ||
    onFullscreen ||
    onLayout ||
    onDrillDown ||
    onDrillUp
  )
  const rightTools = hasRightTools ? (
    <>
      {onScreenshot && (
        <ToolbarButton title="Screenshot" chip={chip} pill={pill} onClick={onScreenshot}>
          <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.screenshot} />
        </ToolbarButton>
      )}
      {onSettings && (
        <ToolbarButton
          title="Settings"
          active={settingsActive}
          dataAction="settings"
          chip={chip}
          pill={pill}
          // Stop the mousedown from reaching SettingsModal's document-level
          // outside-click listener — otherwise opening modal sees the gear
          // click as "outside", closes, and the button's onClick reopens.
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onSettings}
        >
          <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.settings} />
        </ToolbarButton>
      )}
      {onFullscreen && (
        <ToolbarButton title="Fullscreen" chip={chip} pill={pill} onClick={onFullscreen}>
          <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.fullscreen} />
        </ToolbarButton>
      )}
      {onLayout && (
        <ToolbarButton
          title="Layout"
          dataAction="layout"
          active={layoutActive}
          chip={chip}
          pill={pill}
          onClick={onLayout}
        >
          <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.layout} />
        </ToolbarButton>
      )}
      {onDrillUp && (
        <ToolbarButton
          title="Drill up"
          dataAction="drill-up"
          chip={chip}
          pill={pill}
          // Solid purple fill (the inverse of the drill-down affordance) so it
          // reads as a distinct, deliberate exit action.
          tone="primary"
          onClick={onDrillUp}
        >
          <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.cornerRightUp} />
        </ToolbarButton>
      )}
      {onDrillDown && (
        <ToolbarButton
          title="Drill down"
          dataAction="drill-down"
          chip={chip}
          pill={pill}
          onClick={onDrillDown}
        >
          <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.cornerLeftDown} />
        </ToolbarButton>
      )}
    </>
  ) : null

  if (floating) {
    // Glass pill clusters hovering over the chart canvas. The root is a
    // pointer-events-none strip so the chart stays interactive between pills.
    // flex-wrap lets the trailing content (OHLC header) sit beside the chips
    // when the row has room and wrap underneath when it doesn't; the right
    // cluster is out of flow (absolute) so it never wraps, and the padding
    // reserves its corner so first-row content can't slide underneath it.
    //
    // The left chip cluster caps at the available width (max-w-full, minus the
    // reserved right corner) and scrolls horizontally when the panel is too
    // narrow to show every chip, so the rightmost controls stay reachable rather
    // than sliding under the right cluster. Its chip dropdowns portal to <body>
    // (see Popover), so the scroll container's overflow never clips them.
    return (
      <div
        ref={rootRef}
        data-testid="chart-toolbar-root"
        className="pointer-events-none absolute left-1.5 right-1.5 top-1.5 z-30 flex flex-wrap items-center gap-x-1 gap-y-1"
        style={{
          ...(rightReserve != null
            ? { paddingRight: rightReserve }
            : rightTools
              ? { paddingRight: 64 + (rightClusterOffset ?? 0) }
              : null),
          ...(rootTop != null ? { top: rootTop } : null),
        }}
      >
        {!controlsHidden && (
          <FadingScrollRow
            className={`${GLASS_CLUSTER} min-w-0 max-w-full overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
          >
            {leftControls}
          </FadingScrollRow>
        )}
        {trailing}
        {/* Right-side tools only surface while the cursor is over the chart
            (EdgeChart's chart area carries the `group/chart` hover group) — EXCEPT
            while a portaled popover launched from here is open (layout / settings),
            where the cursor sits on the popover (outside the chart's hover scope)
            and the trigger button would otherwise vanish. */}
        {rightTools && (
          <div
            className={`${GLASS_CLUSTER} absolute top-0 transition-opacity duration-150 ${
              layoutActive || settingsActive
                ? 'opacity-100'
                : 'opacity-0 group-hover/chart:opacity-100'
            }`}
            style={{
              right: rightClusterOffset ?? 0,
              ...(rightClusterTop != null ? { top: rightClusterTop } : null),
            }}
          >
            {rightTools}
          </div>
        )}
      </div>
    )
  }

  if (pill) {
    // The workspace panel header's pill, rebuilt for a chart bar outside any
    // panel: one rounded pill holding the title, the segments, and (after a
    // divider) the trailing slot, which hosts the calendar here. Right actions
    // sit flat at the far right in the header's order, Layout then Fullscreen.
    return (
      <div
        data-testid="chart-toolbar-root"
        data-variant="pill"
        className="flex h-8 shrink-0 items-center gap-2 border-b border-border bg-[hsl(var(--chart-canvas))] px-1.5"
      >
        <div className="flex h-6 min-w-0 items-center rounded bg-[hsl(var(--chart-text)/0.08)]">
          {leftControls}
          {trailing != null && (
            <>
              {separator}
              {trailing}
            </>
          )}
        </div>
        {hasRightTools && (
          <div className="ml-auto flex items-center gap-0.5" data-testid="chart-toolbar-right">
            {onLayout && (
              <ToolbarButton
                title="Layout"
                dataAction="layout"
                active={layoutActive}
                pill
                onClick={onLayout}
              >
                <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.layout} />
              </ToolbarButton>
            )}
            {onFullscreen && (
              <ToolbarButton title="Fullscreen" pill onClick={onFullscreen}>
                <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.fullscreen} />
              </ToolbarButton>
            )}
            {onScreenshot && (
              <ToolbarButton title="Screenshot" pill onClick={onScreenshot}>
                <Icon size={iconSize} strokeWidth={iconStroke} d={Icons.screenshot} />
              </ToolbarButton>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      data-testid="chart-toolbar-root"
      className="flex h-8 shrink-0 items-center border-b border-border bg-[hsl(var(--chart-canvas))] px-1"
    >
      {leftControls}
      <div className="ml-auto flex items-center gap-0.5 pr-1">{rightTools}</div>
    </div>
  )
}

/**
 * Standalone purple drill-up chip — the always-visible drill-mode indicator a
 * drill host pins to the ACTIVE cell's top-right. Reuses the Toolbar's
 * primary-tone chip styling + corner-right-up icon so it's pixel-identical to
 * the in-cluster Drill-up button, but it lives OUTSIDE any hover fade so it
 * stays on screen the whole time the drill is open. Clicking it exits the drill.
 *
 * Hosts render this instead of passing `onDrillUp` to the (hover-faded) Toolbar,
 * and pass `rightClusterOffset` to that Toolbar so its hover cluster (Layout,
 * etc.) lands immediately to the LEFT of this pinned chip, never overlapping.
 * Position it with `top-1.5 right-1.5` (matching the floating Toolbar metrics).
 */
export function DrillUpChip({ onClick }: { onClick: () => void }) {
  return (
    <ToolbarButton title="Drill up" dataAction="drill-up" chip tone="primary" onClick={onClick}>
      <Icon size={15} strokeWidth={1.5} d={Icons.cornerRightUp} />
    </ToolbarButton>
  )
}
